import { Skeleton } from 'antd';
import { push } from 'connected-react-router';
import { selectLoading, selectSections } from 'containers/HomePage/selectors';
import { ILoadingSection } from 'containers/HomePage/types';
import useProductUomInfo from 'hooks/useProductUomInfo';
import { GQuery, useLazyQuerySaga } from 'hooks/useQuerySaga';
import React, { FC, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { Product, ProductSortField, QueryProductSearchArgs, SortDirection } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import { commonDimensions } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import generateProductSlug from 'utils/generateProductSlug';
import { productSearch } from 'utils/query';
import { RootState } from './types';

/* ─── Layout ────────────────────────────────────────────────────────────────── */
const SectionContainer = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 767px) {
    margin-bottom: 12px;
    gap: 8px;
  }
`;

/* ─── Card ───────────────────────────────────────────────────────────────────── */
interface CardProps {
  isEssential?: boolean;
}

const Card = styled.div<CardProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 16px;
  padding: 32px 24px;
  overflow: hidden;
  min-height: 203px;
  background: ${({ isEssential }) =>
    isEssential
      ? 'linear-gradient(135deg, #BB4D00 0%, #E17100 100%)'
      : 'linear-gradient(135deg, #008236 0%, #00A63E 100%)'};
  @media (max-width: 1023px) {
    justify-content: space-between;
  }
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 24px;
    padding: 16px 0px;
  }
`;

const BackgroundDecoration = styled.div`
  position: absolute;
  bottom: 0;
  left: 180px;
  opacity: 0.5;
  pointer-events: none;
  line-height: 0;
  @media (max-width: 991px) {
    display: none;
  }
`;

/* ─── Info Panel ─────────────────────────────────────────────────────────────── */
const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 240px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  @media (max-width: 767px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #ffffff;
  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const CardSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.8);
  @media (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
    font-weight: 400;
  }
`;

const DescriptionWrapper = styled.div``;

const DescriptionText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #f5fbf5;
  opcaity: 0.9;
  margin: 0;
  display: block;
  @media (max-width: 767px) {
    display: inline;
    font-size: 12px;
    line-height: 18px;
  }
`;

const CtaButton = styled(Link)`
  cursor: pointer;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #ffffff;
  display: inline-flex;
  &:hover {
    color: #ffffff;
    opacity: 0.85;
  }
  @media (min-width: 768px) {
    margin-top: 16px;
  }
  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 18px;
    margin-left: 2px;
    svg {
      width: 12px;
      height: 12px;
    }
  }
  box-shadow: none;
`;

/* ─── Product Mini-Cards ─────────────────────────────────────────────────────── */
const ProductList = styled.div`
  display: flex;
  gap: 16px;
  margin-left: 25px;
  position: relative;
  z-index: 1;
  flex: 1;
  overflow-x: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox, Safari 18.2+, Chromium 121+ */
  @media (max-width: 1023px) {
    flex: initial;
  }
  @media (max-width: 767px) {
    margin-left: 0px;
    width: 100%;
    padding: 0 16px;
  }
`;

const ProductCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100px;
  flex-shrink: 0;
`;

const ProductImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 16px;
  object-fit: cover;
  display: block;
  background: rgb(255, 255, 255);
`;

const ProductPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #ffffff;
`;

const ProductUom = styled.span`
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.6);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-top: -4px;
  & > div > div {
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px !important;
    margin: 0px !important;
    background: none;
    padding: 0px;
    & > div {
      display: none;
    }
  }
`;

/* ─── Skeleton ──────────────────────────────────────────────────────────────── */
const SkeletonCardItem: FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 100, flexShrink: 0 }}>
    <Skeleton.Input active block style={{ width: 100, height: 100, borderRadius: 16, minWidth: 100 }} />
    <Skeleton.Input active block size="small" style={{ width: 70, marginTop: 4, minWidth: 70 }} />
    {/* <Skeleton.Input active block size="small" style={{ width: 50, marginTop: 2 }} /> */}
  </div>
);

/* ─── Icons ─────────────────────────────────────────────────────────────────── */
const LeafIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M13.4329 6.66733C13.4329 4.71048 13.025 3.44562 12.526 2.34767C12.0132 2.86931 11.4719 3.25377 10.7493 3.56577C9.79127 3.97944 8.52913 4.25926 6.64062 4.62371C6.63676 4.62446 6.63277 4.625 6.6289 4.62567C5.6151 4.79886 4.70295 5.3469 4.07421 6.16082C3.44548 6.97474 3.14591 7.99553 3.23437 9.0202C3.32287 10.0448 3.7932 10.9987 4.55208 11.6927C5.31099 12.3868 6.30293 12.7707 7.33137 12.7676H7.33333C10.5106 12.7675 13.4329 10.0108 13.4329 6.66733ZM14.5664 6.66733C14.5664 10.6568 11.1163 13.8993 7.33398 13.8998L7.33463 13.9004C6.022 13.9043 4.75574 13.4152 3.7871 12.5293C2.81839 11.6433 2.21772 10.4251 2.10481 9.1172C1.99198 7.80939 2.37525 6.50694 3.17773 5.46812C3.98023 4.42931 5.14357 3.72958 6.43749 3.50848C8.34178 3.14082 9.47658 2.88099 10.3001 2.52541C11.0859 2.18611 11.5998 1.75405 12.2253 0.978532C12.3463 0.828427 12.5355 0.750177 12.7272 0.77085C12.9189 0.791543 13.0869 0.908253 13.1732 1.08075C13.858 2.45033 14.5664 3.99518 14.5664 6.66733Z"
      fill="white"
    />
    <path
      d="M0.76709 14.0007C0.76709 11.7257 2.19589 9.92304 4.61019 9.44466C5.34713 9.29845 6.11367 8.98962 6.78076 8.63151C7.45228 8.27101 7.98493 7.88177 8.26644 7.60026L8.66683 7.19922L9.46826 8.00065L9.06722 8.40104C8.68209 8.78617 8.04831 9.2374 7.31657 9.63021C6.58043 10.0254 5.70651 10.3828 4.83024 10.5566C2.93793 10.9316 1.8999 12.2756 1.8999 14.0007V14.5671H0.76709V14.0007Z"
      fill="white"
    />
  </svg>
);

const EssentialIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clip-path="url(#clip0_7989_189361)">
      <path
        d="M8.0708 0.803385C8.21135 0.814827 8.34704 0.860463 8.46598 0.936198L8.52393 0.976563L8.57861 1.02214C8.68359 1.11621 8.76405 1.23434 8.81299 1.36654L8.83512 1.43359L8.83773 1.44466L9.89176 5.53451C9.92749 5.67285 9.99971 5.79935 10.1007 5.90039C10.2016 6.00125 10.3279 6.07296 10.466 6.10872L14.5565 7.16276L14.5656 7.16536C14.7255 7.20953 14.8687 7.29864 14.979 7.42122L15.0246 7.47591L15.0649 7.53385C15.1538 7.67281 15.2016 7.83473 15.2017 8.00065C15.2017 8.19034 15.1395 8.37513 15.0246 8.52604C14.9097 8.6769 14.7484 8.78612 14.5656 8.83659L14.5565 8.83854L10.4666 9.89258C10.3282 9.92829 10.2018 10.0005 10.1007 10.1016C9.99969 10.2026 9.92747 10.329 9.89176 10.4674L8.83708 14.5573C8.83618 14.5608 8.83544 14.5643 8.83447 14.5677C8.7835 14.7499 8.67397 14.9104 8.52327 15.0247C8.37263 15.139 8.1889 15.2012 7.99984 15.2012C7.8107 15.2012 7.62644 15.139 7.47575 15.0247C7.32516 14.9104 7.21615 14.7498 7.1652 14.5677C7.16424 14.5643 7.16349 14.5608 7.1626 14.5573L6.10856 10.4674C6.07286 10.3291 6.00061 10.2026 5.89958 10.1016C5.82367 10.0257 5.73341 9.96588 5.6346 9.92578L5.53369 9.89258L1.44385 8.83789C1.43979 8.83684 1.43551 8.83578 1.43148 8.83464C1.25009 8.78315 1.0901 8.67385 0.9764 8.52344C0.862699 8.37297 0.80127 8.18924 0.80127 8.00065C0.801306 7.81216 0.862785 7.62892 0.9764 7.47852L1.02132 7.42383C1.13052 7.30147 1.27278 7.21236 1.43148 7.16732L1.44385 7.16341L5.53369 6.10807C5.67202 6.07238 5.79852 6.00072 5.89958 5.89974C6.00065 5.79873 6.07279 5.6722 6.10856 5.53385L7.16325 1.44401L7.16585 1.43359C7.21684 1.25153 7.32577 1.09084 7.4764 0.976563L7.53499 0.936198C7.67367 0.847911 7.83512 0.800781 8.00049 0.800781L8.0708 0.803385ZM7.14111 5.80078C7.05764 6.12361 6.88935 6.4186 6.65348 6.6543C6.44714 6.86048 6.19564 7.01493 5.91976 7.10612L5.79997 7.14128L2.46794 8.00065L5.79997 8.86003L5.91976 8.89518C6.19566 8.98642 6.44716 9.14075 6.65348 9.34701C6.88936 9.58288 7.05779 9.87817 7.14111 10.2012L7.99984 13.5332L8.85921 10.2012C8.94254 9.87817 9.11097 9.58288 9.34684 9.34701C9.58267 9.11127 9.87748 8.94332 10.2004 8.86003V8.85938L13.533 8.00065L10.2004 7.14193C9.8774 7.05861 9.5827 6.89012 9.34684 6.6543C9.111 6.41845 8.94255 6.12374 8.85921 5.80078L8.00049 2.4681L7.14111 5.80078Z"
        fill="white"
      />
      <path d="M12.8003 1.4668H13.8667V5.19987H12.8003V1.4668Z" fill="white" />
      <path d="M15.1999 2.80078V3.86719H11.4668V2.80078H15.1999Z" fill="white" />
      <path d="M2.22217 10.8887H3.11149V13.1113H2.22217V10.8887Z" fill="white" />
      <path d="M3.77783 11.5547V12.444H1.55518V11.5547H3.77783Z" fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_7989_189361">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ArrowRightIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M7.1714 2.94822L11.6562 7.32931H2V8.67069H11.6562L7.1714 13.0518L8.14208 14L13.7988 8.47411C14.0671 8.21207 14.0671 7.78793 13.7988 7.52589L8.14208 2L7.1714 2.94822Z"
      fill="white"
    />
  </svg>
);

const BroccoliIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="94" height="74" viewBox="0 0 94 74" fill="none">
    <g clip-path="url(#clip0_7989_189325)">
      <path
        d="M93.0325 40.7868C91.2045 35.5752 87.1318 31.459 82.8851 30.2961C82.9145 23.6341 80.5302 17.7673 76.3399 14.3563C71.5738 10.4801 65.9034 10.5877 61.4778 11.633C60.9436 10.2976 60.0615 8.41199 58.7603 6.55643C55.484 1.89251 51.1589 -0.360676 46.2482 0.0469476C36.3336 0.867197 32.6554 8.00437 30.9842 12.2657C27.2203 11.1703 23.5936 11.0003 20.4006 11.783C17.0263 12.6083 14.321 14.4388 12.7821 16.9346C10.2042 21.1133 9.13825 26.1424 9.86114 30.4337C4.12704 34.1023 0.490545 39.6815 0.0470101 45.5983C-0.349966 50.8999 1.83095 55.7039 6.18789 59.1249C9.3784 61.6282 12.8091 62.9086 16.2667 62.906C17.3963 62.906 18.5309 62.7685 19.6581 62.4934C21.0181 62.1608 22.3365 61.6282 23.596 60.9204C25.2991 62.886 29.2027 67.8275 31.2978 73.9344C34.035 81.9168 33.5474 91.7073 33.5425 91.8048L33.4273 93.9155H60.2918L59.8409 91.5322C59.8189 91.4147 57.6919 79.7887 62.4237 70.5259C65.4353 64.6291 67.2511 61.6031 68.2068 60.1452C69.383 60.968 70.672 61.6132 72.0614 62.0533C77.6607 63.8338 83.8751 62.0558 89.1069 57.1743C93.7407 52.853 95.1718 46.8762 93.035 40.7818L93.0325 40.7868ZM58.9465 68.6828C54.8983 76.6077 55.2144 85.6905 55.6604 89.9193H37.5049C37.5122 86.3982 37.2206 79.101 34.9981 72.619C32.7755 66.142 28.8474 60.983 26.8208 58.6022C28.4308 57.1593 29.8815 55.3913 31.1092 53.3481C32.5329 54.596 34.1208 55.6113 35.8459 56.3891L46.6892 77.1979L56.5034 55.6889C58.601 54.7686 60.5712 53.6232 62.3012 52.2953C63.051 54.2484 64.0582 55.9739 65.2785 57.4394C64.2542 58.9473 62.2791 62.1608 58.9489 68.6853L58.9465 68.6828ZM41.0948 57.9445C42.0456 58.0971 43.0233 58.1921 44.028 58.2246C46.4344 58.3022 48.9363 58.0146 51.3843 57.4194L46.4515 68.2277L41.0924 57.9445H41.0948ZM86.4579 54.2259C82.2823 58.1221 77.4573 59.585 73.2254 58.2396C69.2581 56.9792 66.3322 53.3681 65.1976 48.3366L64.4037 44.8156L61.8993 47.3713C57.6698 51.6876 50.5389 54.4435 44.153 54.2284C40.7076 54.1134 35.819 53.0681 32.1016 48.7367L30.1412 46.4561L28.8596 49.1944C26.5243 54.1784 22.8364 57.6094 18.7441 58.6072C15.3551 59.435 11.8387 58.5172 8.57709 55.9564C5.30327 53.3857 3.66145 49.8171 3.95551 45.9084C4.33043 40.9119 7.72433 36.1504 13.032 33.1695L14.4068 32.3968L13.9681 30.8588C12.9806 27.3903 13.8162 22.7614 16.0976 19.0677C17.4086 16.9421 20.3761 15.2941 24.5321 15.2941C26.5807 15.2941 28.9184 15.6942 31.4865 16.637L33.3072 17.3047L34.1085 15.119C35.4293 11.4979 37.8847 4.75588 46.5618 4.03816C50.0488 3.75057 53.0556 5.35606 55.5109 8.80961C57.4909 11.598 58.3486 14.5714 58.3584 14.6014L58.9048 16.5545L60.8113 15.9618C66.1166 14.3163 70.6401 14.8464 73.8919 17.4922C77.45 20.3881 79.3246 25.7523 78.9031 31.8416L78.7634 33.8722L80.7532 33.9823C84.0246 34.1623 87.7959 37.7459 89.3348 42.1422C90.2635 44.788 91.1211 49.8771 86.453 54.2334L86.4579 54.2259Z"
        fill="white"
        fill-opacity="0.2"
      />
      <path
        d="M28.5652 25.5694C26.524 27.38 25.3894 29.8833 25.1934 33.0117L29.1068 33.2693C29.2342 31.2287 29.8958 29.7032 31.1235 28.6029C33.5813 26.4047 37.5266 26.6498 37.5682 26.6523L37.8549 22.6611C37.6295 22.6436 32.2777 22.2759 28.5677 25.5694H28.5652Z"
        fill="white"
        fill-opacity="0.2"
      />
      <path
        d="M64.3029 25.5694C60.5929 22.2759 55.2411 22.6436 55.0156 22.6611L55.3023 26.6523C55.3415 26.6523 59.2868 26.4047 61.7471 28.6029C62.9747 29.7007 63.6339 31.2262 63.7638 33.2693L67.6772 33.0117C67.4812 29.8858 66.3466 27.3825 64.3054 25.5694H64.3029Z"
        fill="white"
        fill-opacity="0.2"
      />
    </g>
    <defs>
      <clipPath id="clip0_7989_189325">
        <rect width="94" height="74" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const EssentialBgIcon: FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="73" height="65" viewBox="0 0 73 65" fill="none">
      <g opacity="0.2" clip-path="url(#clip0_7989_189348)">
        <path
          d="M69.5688 42.3193V62.8812C69.6757 64.4991 69.0396 68.4408 65.6401 71.2648C61.3906 74.7947 54.335 76.7362 47.0388 76.7362C39.7426 76.7362 34.9319 75.0595 29.0789 68.9703C26.5132 65.9699 26.5132 64.2049 26.5132 62.0869C26.5132 60.3926 26.5132 47.7318 26.5132 41.6133"
          stroke="white"
          stroke-width="3"
        />
        <path
          d="M31.1684 33.4142H14.6232C13.8667 33.3125 12.3539 33.821 12.3539 36.6682C12.3539 39.5155 12.3539 57.7176 12.3539 66.4628C12.1648 67.4797 12.4025 69.5134 14.8663 69.5134C17.3301 69.5134 24.3426 69.5134 27.3954 69.5134"
          stroke="white"
          stroke-width="3"
        />
        <path
          d="M43.1102 76.5602C35.2795 76.5602 11.7926 76.5602 8.07237 76.5602C4.35211 76.5602 2.48656 73.1184 2.6202 71.177C2.35294 57.5573 2.37962 27.3704 3.02104 19.8163C3.66247 12.2623 9.08776 9.05014 10.6379 8.0794V3.22574C10.6379 2.75508 10.9906 1.81376 12.4018 1.81376C13.8129 1.81376 17.9608 1.81376 19.8583 1.81376C20.4463 1.75493 21.6222 1.95496 21.6222 3.22574C21.6222 4.49652 21.6222 6.99101 21.6222 8.0794H23.2258C23.6534 5.99086 25.4708 1.81376 29.3193 1.81376C34.13 1.81376 41.0111 3.22574 46.3171 11.9622C49.1708 16.661 49.0431 24.4053 49.0431 27.3175"
          stroke="white"
          stroke-width="3"
        />
        <path
          d="M69.649 41.0851C69.649 48.6883 59.9927 54.8519 48.0811 54.8519C36.1695 54.8519 26.5132 48.6883 26.5132 41.0851C26.5132 33.482 36.1695 27.3184 48.0811 27.3184C59.9927 27.3184 69.649 33.482 69.649 41.0851Z"
          stroke="white"
          stroke-width="3"
        />
        <line x1="40.46" y1="41.8594" x2="55.8802" y2="41.8594" stroke="white" stroke-width="3" />
        <path d="M28.8955 12.5537C32.5605 11.851 39.5241 14.2401 38.0581 29.4176" stroke="white" stroke-width="3" />
      </g>
      <defs>
        <clipPath id="clip0_7989_189348">
          <rect width="73" height="65" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

/* ─── Interfaces ─────────────────────────────────────────────────────────────── */
interface IOwnProps {
  isProductDetail?: boolean;
}

interface StateProps {
  sections: any;
  loading: ILoadingSection | boolean;
}

interface DispatchProps {
  gotoSignIn: () => void;
  goToKamereoFresh: () => void;
  goToKamereoEssential: () => void;
}

type IProps = StateProps & DispatchProps & IOwnProps;

interface IKamereoProductProps {
  isEssential?: boolean;
  data: Product[];
  loading?: boolean;
}

/* ─── KamereoProduct ─────────────────────────────────────────────────────────── */
const KamereoProduct: FC<IKamereoProductProps> = ({ isEssential, data, loading }) => {
  const lang = window.localStorage.getItem('lang');
  const isVi = lang === 'vi';
  const isLoading = data?.every((dt) => typeof dt === 'number' && dt === 0);
  const title = isEssential
    ? isVi
      ? 'Kamereo Tiện Dụng'
      : 'Kamereo Essential'
    : isVi
      ? 'Kamereo Ngon'
      : 'Kamereo Fresh';

  const subtitle = isEssential
    ? isVi
      ? 'Vật dụng thiết yếu Kamereo'
      : 'Daily must-haves by Kamereo'
    : isVi
      ? 'Thực phẩm tươi sạch Kamereo'
      : 'Clean, fresh food by Kamereo';

  const description = isEssential
    ? isVi
      ? 'Tối ưu vận hành, Thân thiện\nmôi trường, Rẻ hơn khi mua thùng.'
      : 'Operational efficiency, eco-friendly,\nlower prices by the case.'
    : isVi
      ? 'Tiện lợi, An toàn vệ sinh thực phẩm,\nTươi ngon mỗi ngày.'
      : 'Convenient, safe, and hygienic —\nfresh every day.';

  const ctaLabel = isVi ? 'Khám phá ngay' : 'Explore Now';

  const displayProducts = isLoading ? [] : (data as Product[]).slice(0, 3);

  const generateUomInfo = (product) => {
    const { uomInfo } = useProductUomInfo(product);
    return uomInfo;
  };
  return (
    <Card isEssential={isEssential}>
      {/* Background decoration */}
      <BackgroundDecoration>{isEssential ? <EssentialBgIcon /> : <BroccoliIcon />}</BackgroundDecoration>
      {/* Left info panel */}
      <InfoPanel>
        <HeaderRow>
          <IconContainer>{isEssential ? <EssentialIcon /> : <LeafIcon />}</IconContainer>
          <TitleGroup>
            <CardTitle>{title}</CardTitle>
            <CardSubtitle>{subtitle}</CardSubtitle>
          </TitleGroup>
        </HeaderRow>
        <DescriptionWrapper>
          <DescriptionText>{description}</DescriptionText>
          <CtaButton to={`/${isEssential ? 'kamereo-tien-dung' : 'kamereo-ngon'}`}>
            {ctaLabel} <ArrowRightIcon />
          </CtaButton>
        </DescriptionWrapper>
      </InfoPanel>
      {/* Right mini product cards */}
      <ProductList>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SkeletonCardItem />
            <SkeletonCardItem />
            <SkeletonCardItem />
          </div>
        ) : (
          displayProducts.map((product: Product) => (
            <ProductCard
              key={product.id}
              to={`/products/${generateProductSlug(product?.name, product?.supplierInternalCode, lang)}`}
            >
              <ProductImage
                src={product.imageUrl || ''}
                alt={product.name || ''}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              <ProductPrice>
                {formatMoney(
                  calculateVAT(
                    product?.priceV2?.originalPrice || 0,
                    product?.priceV2?.vat || 0,
                    product?.priceV2?.vatIncluded || false,
                  ).beforeVat,
                )}
                ₫
              </ProductPrice>
              <ProductUom>{generateUomInfo(product)}</ProductUom>
            </ProductCard>
          ))
        )}
      </ProductList>
    </Card>
  );
};

/* ─── Module-level cache ─────────────────────────────────────────────────────── */
const PS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes
let _freshCache: { data: Product[]; ts: number } | null = null;
let _essentialCache: { data: Product[]; ts: number } | null = null;

/* ─── KamereoProductSection ──────────────────────────────────────────────────── */
const KamereoProductSection: FC<IProps> = (props) => {
  const kamereoFreshCatIds = [1, 3, 16, 17, 33, 69];
  const kamereoEssentialCatIds = [52, 101, 102, 103];
  const [freshData, setFreshData] = useState([] as Product[]);
  const [essentialData, setEssentialData] = useState([] as Product[]);
  const [isFetching, setIsFetching] = useState(() => {
    const now = Date.now();
    return !(
      _freshCache &&
      now - _freshCache.ts < PS_CACHE_TTL &&
      _essentialCache &&
      now - _essentialCache.ts < PS_CACHE_TTL
    );
  });

  const [fetchProducts] = useLazyQuerySaga<GQuery<'productSearch'>, QueryProductSearchArgs>(productSearch, {
    authentication: false,
  });

  useEffect(() => {
    const now = Date.now();
    const freshValid = _freshCache && now - _freshCache.ts < PS_CACHE_TTL;
    const essentialValid = _essentialCache && now - _essentialCache.ts < PS_CACHE_TTL;
    if (freshValid && essentialValid) {
      setFreshData(_freshCache!.data);
      setEssentialData(_essentialCache!.data);
      setIsFetching(false);
      return;
    }
    (async () => {
      const [kamereoFreshData, kamereoEssentialData] = await Promise.all([
        fetchProducts({
          variables: {
            filter: {
              tags: ['THUONG_HIEU_KAMEREO'],
              categoryIds: kamereoFreshCatIds,
              withexclusive: true,
            },
            sort: [
              {
                field: ProductSortField.CreatedAt,
                order: SortDirection.Desc,
              },
            ],
            pagination: { page: 0, size: 10 },
          },
        }),
        fetchProducts({
          variables: {
            filter: {
              tags: ['THUONG_HIEU_KAMEREO'],
              categoryIds: kamereoEssentialCatIds,
            },
            sort: [
              {
                field: ProductSortField.CreatedAt,
                order: SortDirection.Desc,
              },
            ],
            pagination: { page: 0, size: 10 },
          },
        }),
      ]);
      if (kamereoFreshData?.data) {
        const fresh = kamereoFreshData.data.slice(0, 3) as Product[];
        _freshCache = { data: fresh, ts: Date.now() };
        setFreshData(fresh);
      }
      if (kamereoEssentialData?.data) {
        const essential = kamereoEssentialData.data.slice(0, 3) as Product[];
        _essentialCache = { data: essential, ts: Date.now() };
        setEssentialData(essential);
      }
      setIsFetching(false);
    })();
  }, []);

  return (
    <SectionContainer className="container-padding">
      <KamereoProduct data={freshData} loading={isFetching} />
      <KamereoProduct data={essentialData} isEssential loading={isFetching} />
    </SectionContainer>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  sections: selectSections(),
  loading: selectLoading(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    gotoSignIn: () => dispatch(push('/signin')),
    goToKamereoFresh: () => dispatch(push('/kamereo-ngon')),
    goToKamereoEssential: () => dispatch(push('/kamereo-tien-dung')),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(injectIntl(KamereoProductSection));
